/**
 * Client-side .xlsx export (owner 2026-10-28, target tile drill-down).
 *
 * `exportRowsToXlsx` writes ONE sheet:
 *   1. title rows (e.g. metric, period, currency, rates basis), one per line;
 *   2. a blank spacer row (only when there are title rows);
 *   3. the header row (column labels);
 *   4. one row per data row, in the order given;
 *   5. an optional totals row ("Total" in the first column unless `totals`
 *      names a value for it).
 * and then triggers the browser download.
 *
 * Rules:
 *   - SheetJS (`xlsx`, already a dependency) is lazy-imported exactly like
 *     AttachmentViewer / account-lead-history do, so it stays out of the main
 *     bundle. No new dependency.
 *   - Money is a decimal string everywhere in SEED EMERGE. The ONLY place a
 *     decimal string becomes a JS number is `decimalStringToExportNumber`
 *     below, at the export boundary, so Excel can sum and format the cell.
 *     That number is DISPLAY-ONLY: never feed it back into any calculation.
 *   - A column with `numFmt` is a numeric column: string values that are plain
 *     decimals become numbers with that format; anything else (including a
 *     non-decimal string) is written as text, and `null` is an empty cell.
 *   - Callers decide which rows and columns go in. The drill-down passes ALL
 *     rows the viewer can see and ALL columns, ignoring on-screen filters and
 *     hidden columns (owner decision 3).
 */

export interface XlsxExportColumn<Row> {
  /** Stable column id; also the key into `totals`. */
  key: string;
  /** Header text. */
  label: string;
  /** Cell value. Strings stay text unless the column has `numFmt`. */
  value: (row: Row) => string | number | null;
  /** Excel number format (e.g. MONEY_NUMBER_FORMAT); marks the column numeric. */
  numFmt?: string;
}

export interface XlsxExportOptions<Row> {
  /** Download file name; `.xlsx` is appended when missing. */
  fileName: string;
  /** Sheet tab name; trimmed to Excel's 31-character limit and cleaned. */
  sheetName: string;
  /** Title line(s) written above the header row. Empty strings are skipped. */
  title: string | readonly string[];
  columns: readonly XlsxExportColumn<Row>[];
  rows: readonly Row[];
  /** Totals row values by column key (decimal strings for numeric columns). */
  totals?: Record<string, string>;
}

/** Two-decimal money format with thousands grouping. */
export const MONEY_NUMBER_FORMAT = '#,##0.00';

/** Integer count format with thousands grouping. */
export const COUNT_NUMBER_FORMAT = '#,##0';

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

/**
 * DISPLAY-ONLY conversion of a decimal string to a JS number, used solely to
 * write a numeric spreadsheet cell. Returns null for anything that is not a
 * plain decimal ("", "—", "1,000", null). Never use the result in arithmetic:
 * money maths stays in lib/decimal (BigInt).
 */
export function decimalStringToExportNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) return null;
  // Export boundary: the one sanctioned decimal-string → number conversion.
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

type CellValue = string | number | null;

interface CellObject {
  t: 's' | 'n' | 'z';
  v?: string | number;
  z?: string;
}

function toCell(value: CellValue, numFmt: string | undefined): CellObject {
  if (value === null) return { t: 'z' };
  if (numFmt !== undefined) {
    const n = typeof value === 'number' ? value : decimalStringToExportNumber(value);
    if (n !== null && Number.isFinite(n)) return { t: 'n', v: n, z: numFmt };
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { t: 'n', v: value } : { t: 'z' };
  }
  return { t: 's', v: value };
}

function cleanSheetName(name: string): string {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').replace(/\s+/g, ' ').trim();
  return (cleaned || 'Sheet1').slice(0, 31);
}

function cleanFileName(name: string): string {
  const base = name.replace(/[\\/:*?"<>|]/g, '-').trim() || 'seed-emerge-export';
  return /\.xlsx$/i.test(base) ? base : `${base}.xlsx`;
}

/** Build the workbook and trigger the download. Rejects if SheetJS fails to load. */
export async function exportRowsToXlsx<Row>(options: XlsxExportOptions<Row>): Promise<void> {
  const { columns, rows, totals } = options;
  const XLSX = await import('xlsx');

  const titleLines = (typeof options.title === 'string' ? [options.title] : options.title).filter(
    (line) => line.trim() !== '',
  );

  const grid: CellObject[][] = [];
  for (const line of titleLines) grid.push([{ t: 's', v: line }]);
  if (titleLines.length > 0) grid.push([]);

  const headerRowIndex = grid.length;
  grid.push(columns.map((column) => ({ t: 's', v: column.label })));

  for (const row of rows) {
    grid.push(columns.map((column) => toCell(column.value(row), column.numFmt)));
  }

  if (totals) {
    grid.push(
      columns.map((column, index) => {
        const total = totals[column.key];
        if (total !== undefined) return toCell(total, column.numFmt);
        return index === 0 ? { t: 's', v: 'Total' } : { t: 'z' };
      }),
    );
  }

  const sheet = XLSX.utils.aoa_to_sheet(grid as unknown as unknown[][]);

  // Column widths from the longest header/data text, capped for readability.
  sheet['!cols'] = columns.map((column, colIndex) => {
    let width = column.label.length;
    for (let r = headerRowIndex + 1; r < grid.length; r += 1) {
      const cell = grid[r]?.[colIndex];
      if (cell?.v !== undefined) width = Math.max(width, String(cell.v).length);
    }
    return { wch: Math.min(Math.max(width + 2, 8), 60) };
  });

  // Excel filter buttons on the header row (which sits below the title rows),
  // covering the data rows but not the totals row.
  sheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRowIndex, c: 0 },
      e: { r: headerRowIndex + rows.length, c: Math.max(columns.length - 1, 0) },
    }),
  };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, cleanSheetName(options.sheetName));
  XLSX.writeFile(workbook, cleanFileName(options.fileName), { compression: true });
}
